import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { withSiteData } from 'react-static';
import Chip from '../brand/Chip';
import BrandButton from '../brand/BrandButton';
import { Section, Inner, SectionTitle, Cover, Dots, SlideCarousel, slickFill } from './shared';
import { media } from '../../styles/style-utils';
import { type } from '../../styles/typography';
import albumCover from '../../assets/images/home/album-cover.jpg';
import mahlerLieder from '../../assets/images/home/album-mahler-lieder.jpg';
import theSource from '../../assets/images/home/album-the-source.jpg';
import tristanGeneration from '../../assets/images/home/album-tristan-generation.jpg';
import videoStill from '../../assets/images/home/video-still.jpg';

const ALBUMS = [
  {
    id: 'barber-zorn-glass',
    title: 'Barber Zorn Glass',
    image: albumCover,
    slug: 'barber-and-glass-violin-concertos-zorn-partita-for-the-firebird/',
  },
  {
    id: 'mahler-lieder',
    title: 'Mahler: Lieder',
    image: mahlerLieder,
    slug: 'mahler-lieder-6F334F17-81AA-4D85-B982-C50FB1EB2477',
  },
  { id: 'the-source', title: 'The Source', image: theSource, slug: 'the-source' },
  {
    id: 'tristan-generation',
    title: 'Tristan Generation',
    image: tristanGeneration,
    slug: 'tristan-generation',
  },
];

const Wrapper = styled(Section)`
  padding-top: 64px;
  padding-bottom: 68px;
  background-color: var(--b-white);
  color: var(--b-black);

  ${media.large`
    padding-top: 88px;
  `};
`;

const Content = styled(Inner)`
  display: flex;
  flex-direction: column;
  gap: 48px;

  ${media.large`
    gap: 80px;
  `};
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;

  ${media.mediumLarge`
    grid-template-columns: 1fr 1fr;
  `};

  ${media.large`
    grid-template-columns: 458px 1fr;
  `};
`;

const ARTWORK_HEIGHT = 293;
const META_PADDING = 22;

const Album = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 428px;
  border-radius: var(--b-radius);
  overflow: hidden;
  background-color: var(--b-aqua);

  ${slickFill};

  .artwork {
    position: relative;
    height: ${ARTWORK_HEIGHT}px;
    overflow: hidden;
    background-color: #080808;

    img {
      position: absolute;
      top: -24px;
      left: 0;
      width: 100%;
      height: auto;
      aspect-ratio: 1;
      object-fit: cover;
    }
  }

  .badge {
    position: absolute;
    top: 20px;
    left: 20px;
  }

  .meta {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${META_PADDING}px;
    padding: ${META_PADDING}px;
    border-radius: var(--b-radius);
    background-color: var(--b-light-blue);

    h3 {
      ${type('headline', 'small')};
      margin: 0;
      padding: 0 66px 0 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--b-black);
    }
  }

  .actions {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: auto;
  }
`;

const DotsSlot = styled.div`
  ${type('headline', 'small')};
  position: absolute;
  top: ${ARTWORK_HEIGHT + META_PADDING}px;
  right: ${META_PADDING}px;
  z-index: 1;
  display: flex;
  align-items: center;

  &::before {
    content: '\\200b';
    width: 0;
  }
`;

const Video = styled.article`
  display: flex;
  flex-direction: column;
  min-height: 428px;
  border-radius: var(--b-radius);
  overflow: hidden;
  background-color: var(--b-black);
  color: var(--b-white);

  ${media.large`
    display: grid;
    grid-template-columns: 1fr 1fr;
  `};

  .text {
    display: flex;
    flex-direction: column;
    gap: 48px;
    padding: 20px 20px 24px;

    ${media.mediumLarge`
      flex: 0 0 ${ARTWORK_HEIGHT}px;
      height: ${ARTWORK_HEIGHT}px;
      max-height: ${ARTWORK_HEIGHT}px;
      min-height: 0;
      gap: 16px;
      justify-content: space-between;
      overflow: hidden;
    `};

    ${media.large`
      flex: none;
      height: auto;
      max-height: none;
      gap: 48px;
      justify-content: flex-start;
      overflow: visible;
    `};

    ${media.extraLarge`
      padding-right: 118px;
    `};

    h3 {
      ${type('headline', 'small')};
      flex: 1 1 auto;
      margin: 0;
      padding: 0;
      color: var(--b-white);

      ${media.mediumLarge`
        flex: 0 1 auto;
      `};

      ${media.large`
        flex: 1 1 auto;
      `};
    }
  }

  .still {
    position: relative;
    height: 260px;
    overflow: hidden;
    background-color: #b4b3b3;

    ${media.mediumLarge`
      flex: 1 1 auto;
      height: auto;
      min-height: 0;
    `};

    ${media.large`
      flex: none;
      height: auto;
    `};

    img {
      transform: scale(1.3);
      transform-origin: 100% 5%;
    }
  }
`;

const AlbumCard = ({ appDomain }) => (
  <Album>
    <SlideCarousel
      speed={900}
      renderDots={(active, select) => (
        <DotsSlot>
          <Dots count={ALBUMS.length} active={active} onSelect={select} label="Albums" />
        </DotsSlot>
      )}
    >
      {ALBUMS.map(album => (
        <div key={album.id} className="slide">
          <div className="artwork">
            <img src={album.image} alt="" draggable="false" />
          </div>
          <div className="meta">
            <h3>{album.title}</h3>
            <div className="actions">
              <BrandButton
                to={`${appDomain}/albums/${album.slug}`}
                openInNewTab
                tone="black"
                size="medium"
                trackingData={{ title: 'Listen', from: 'Home Player' }}
              >
                <FormattedMessage id="home.player.listen" defaultMessage="Listen" />
              </BrandButton>
            </div>
          </div>
        </div>
      ))}
    </SlideCarousel>
    <Chip tone="dark" uppercase className="badge">
      <FormattedMessage id="home.player.audio" defaultMessage="Audio" />
    </Chip>
  </Album>
);

AlbumCard.propTypes = {
  appDomain: PropTypes.string.isRequired,
};

const Player = ({ appDomain }) => (
  <Wrapper>
    <Content>
      <SectionTitle>
        <FormattedMessage
          id="home.player.title"
          defaultMessage="The Work. The Performance.{br}And everything in-between."
          values={{ br: <br /> }}
        />
      </SectionTitle>
      <Cards>
        <AlbumCard appDomain={appDomain} />
        <Video>
          <div className="text">
            <div>
              <Chip tone="dark" uppercase>
                <FormattedMessage id="home.player.video" defaultMessage="Video" />
              </Chip>
            </div>
            <h3>
              <FormattedMessage
                id="home.player.video.title"
                defaultMessage="Discover Bizet's Carmen in Andreas Homoki's visionary production."
              />
            </h3>
            <div>
              <BrandButton
                to={`${appDomain}/live/event/georges-bizet-carmen-opera-comique`}
                openInNewTab
                tone="white"
                size="medium"
                trackingData={{ title: 'Watch', from: 'Home Player' }}
              >
                <FormattedMessage id="home.player.watch" defaultMessage="Watch" />
              </BrandButton>
            </div>
          </div>
          <div className="still">
            <Cover src={videoStill} alt="" />
          </div>
        </Video>
      </Cards>
    </Content>
  </Wrapper>
);

Player.propTypes = {
  appDomain: PropTypes.string.isRequired,
};

export default withSiteData(Player);



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/home/Player.js